import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { api, useAdminApi, errMsg } from "@/lib/api";
import { MONTHS, seasonStatus } from "@/lib/site";
import { SeasonBadge } from "@/components/site/SeasonCalendar";

const ProductEditor = ({ product, onSaved }) => {
  const call = useAdminApi();
  const [months, setMonths] = useState(product.available_months);
  const [show, setShow] = useState(product.show_calendar);
  const [busy, setBusy] = useState(false);
  const toggle = (m) => setMonths((ms) => (ms.includes(m) ? ms.filter((x) => x !== m) : [...ms, m].sort((a, b) => a - b)));
  const save = async () => {
    setBusy(true);
    try {
      await call({ method: "put", url: `/admin/products/${product.slug}`, data: { available_months: months, show_calendar: show } });
      toast.success(`${product.name} updated`);
      onSaved();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="bg-white border border-stone-200 rounded-md p-6 space-y-5" data-testid={`availability-editor-${product.slug}`}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-4">
          <img src={product.image} alt="" className="h-12 w-12 rounded-md object-cover" />
          <div><h2 className="text-lg font-semibold">{product.name}</h2><p className="text-xs text-stone-500">{product.variety}</p></div>
        </div>
        <SeasonBadge status={seasonStatus({ available_months: months })} testid={`availability-status-${product.slug}`} />
      </div>
      <div className="grid grid-cols-6 sm:grid-cols-12 gap-1.5">
        {MONTHS.map((m, i) => (
          <button key={m} onClick={() => toggle(i + 1)} data-testid={`availability-month-${product.slug}-${i + 1}`}
            className={`rounded-md py-2.5 text-xs font-semibold transition-colors ${months.includes(i + 1) ? "bg-emerald-500 text-white" : "bg-stone-100 text-stone-500 hover:bg-stone-200"}`}>
            {m}
          </button>
        ))}
      </div>
      <div className="flex flex-wrap items-center justify-between gap-4">
        <label className="flex items-center gap-3 text-sm">
          <Switch checked={show} onCheckedChange={setShow} data-testid={`availability-calendar-switch-${product.slug}`} />
          Show season calendar on product page
        </label>
        <button onClick={save} disabled={busy} data-testid={`availability-save-btn-${product.slug}`}
          className="rounded-full bg-emerald-900 hover:bg-emerald-800 disabled:opacity-60 text-white px-5 py-2 text-sm transition-colors">
          Save
        </button>
      </div>
    </div>
  );
};

export default function AdminProducts() {
  const qc = useQueryClient();
  const { data: products = [] } = useQuery({ queryKey: ["products"], queryFn: async () => (await api.get("/products")).data });
  return (
    <div className="space-y-6 max-w-4xl">
      <div>
        <h1 className="text-3xl font-semibold tracking-tight">Availability</h1>
        <p className="text-sm text-stone-500">Click months to mark when each product ships. Out-of-season products show "Next season: Month" on the site.</p>
      </div>
      {products.map((p) => <ProductEditor key={p.slug} product={p} onSaved={() => qc.invalidateQueries({ queryKey: ["products"] })} />)}
    </div>
  );
}
